// auth.js
// Centralised logout. Must be BULLETPROOF and Microsoft-safe: an early version
// awaited msalInstance.logoutRedirect(), which stalls/redirects for a Microsoft
// account so the follow-up navigation never ran and the dashboard stayed on
// screen (Google had no MSAL account, so it worked by accident).
//
// Two things have to happen, in this order:
//
//   1. Tell the BACKEND to end the session. Our JWT is stateless, so wiping it
//      out of the browser does not make it stop working — it stays signed and
//      unexpired for the rest of its hour, and anything that copied it (Burp,
//      devtools, a shared machine) could keep using it on a logged-out session.
//      That was the VAPT "Login Bypass / Improper Session Management" finding.
//      POST /api/auth/logout/ records the logout server-side, after which the
//      API refuses that token and every other token issued before now.
//
//   2. Wipe local storage and hard-reload to "/" — which also clears the MSAL
//      cache, since MSAL uses cacheLocation: "sessionStorage" — so the app boots
//      clean with an empty MSAL cache and a fresh Redux store.
//
// The server call is given a short budget and sent with keepalive, so a slow or
// failed network delays the redirect by at most LOGOUT_TIMEOUT_MS and never
// traps the user on the dashboard. keepalive lets the request finish even though
// we navigate away immediately afterwards.

import { msalInstance } from "./msalConfig";
import { clearSession, getToken } from "./session";

const API_BASE =
  process.env.REACT_APP_API_URL || "https://api-project360-insights.haqdarshak.com";

const LOGOUT_TIMEOUT_MS = 2500;

// Revoke the token server-side. Never throws — logout must not be blockable.
const revokeTokenOnServer = async (token) => {
  if (!token) return;

  // The Authorization header is set explicitly rather than relying on the
  // apiClient interceptor, because the session is cleared moments later.
  const request = fetch(`${API_BASE}/api/auth/logout/`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
    keepalive: true,
  }).catch((err) => {
    console.error("Logout: could not reach the server to end the session.", err);
  });

  // Wait, but not indefinitely. Not aborted on timeout — keepalive means the
  // request still completes in the background after we navigate away.
  await Promise.race([
    request,
    new Promise((resolve) => setTimeout(resolve, LOGOUT_TIMEOUT_MS)),
  ]);
};

// Guards against a second click while the revoke request is in flight — the
// second call would have no token left to revoke.
let logoutInFlight = false;

export const logoutUser = async () => {
  if (logoutInFlight) return;
  logoutInFlight = true;

  // Read the token BEFORE anything clears it.
  let token = null;
  try { token = getToken(); } catch { /* ignore */ }

  // 1) End the session on the server.
  await revokeTokenOnServer(token);

  // 2) Our app session + remembered route.
  try { clearSession(); } catch { /* ignore */ }
  try { sessionStorage.removeItem("lastPath"); } catch { /* ignore */ }

  // 3) Drop MSAL's in-memory active account so nothing reuses it before reload.
  try { msalInstance.setActiveAccount(null); } catch { /* ignore */ }

  // 4) Wipe everything MSAL (and anything else) persisted.
  try { sessionStorage.clear(); } catch { /* ignore */ }
  try { localStorage.clear(); } catch { /* ignore */ }

  // 5) Hard redirect to login — a full reload resets MSAL + Redux cleanly.
  window.location.replace("/");
};
